import { bugsnagClient } from "@src/bugsnag";
import { isDev } from "@src/env";
import { kebabCase, startCase } from "lodash";
import * as React from "react";
import type { DesignDocUploadStatus, DocumentGroup } from "../../../../types";
import { DocumentsState } from "./types";

export function getNodeDropdownOptions({
  id,
  label = id,
  tailwindColor,
}: {
  id: string;
  label?: string;
  tailwindColor: string;
}) {
  return {
    id: id,
    label: label,
    nodeLabel: (
      <div className="flex w-full flex-row items-center space-x-2 text-gray400 hover:text-charcoal500">
        <div className={`h-2 w-2 shrink-0 rounded ${tailwindColor}`} />
        <p className="truncate font-normal">{label}</p>
      </div>
    ),
  };
}

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

export const displayUploadingStatus = (
  processing: boolean,
  userCanceled: boolean,
  status: DesignDocUploadStatus,
  percentUploaded: number
): string | React.ReactNode => {
  if (processing) return "Processing";
  if (userCanceled && status === "failed") return "Canceled";
  if (status === "uploading")
    return (
      <div className=" h-1.5 w-24 bg-gray275">
        <div
          className="h-1.5 bg-blue transition-all"
          style={{
            width: `${percentUploaded}%`,
          }}
        ></div>
      </div>
    );
  return `${startCase(status)}${status === "failed" ? " To Upload" : ""}`;
};

// Special path mappings for some of the parent groups
const parentGroupPathMapping: { [groupname: string]: string | undefined } = {
  "O&M": "operations-and-management",
  "CapEx Plan": "capex-plan",
};
// Strip non-(alphanumeric and &) characters
export const getParentGroupPath = (groupname: string) => {
  const customPath = parentGroupPathMapping[groupname];
  if (customPath) {
    return customPath;
  }
  return kebabCase(groupname.replace(/['[^a-zA-Z0-9& -]']/, ""));
};

export const getGroupPath = (
  group: DocumentGroup,
  subgroup?: DocumentGroup
) => {
  const parentPath = getParentGroupPath(group.groupname);

  if (!subgroup) {
    return parentPath;
  }

  return [parentPath, subgroup?.id].join("/");
};

// If we get a lot of logs with uncataloged file extensions (seems unlikely)
// we can consider not hardcoding these

/**
 * Based on document types stored in DB 1/2026
 */
const COMMON_INLINE_FILE_EXTENSIONS: readonly string[] = [
  // plain text
  "pcp",
  "url",
  "txt",
  "log",

  // code/markdown
  "md",
  "json",
  "xml",
  "html",

  // video
  "mp4",
  "m4v",
  "webm",

  // image
  "jpg",
  "jpeg",
  "png",
  "webp",
  "svg",
  "gif",
  "bmp",

  // pdf
  "pdf",
];

/**
 * Based on document types stored in DB 1/2026
 */
const COMMON_DOWNLOAD_FILE_EXTENSIONS: readonly string[] = [
  // office/documents
  "docx",
  "doc",
  "xlsx",
  "xlsm",
  "xlsb",
  "xls",
  "csv",
  "pptx",
  "rtf",
  "msg",
  "eml",

  // engineering/cad/design
  "dwg",
  "rvt",
  "tif",
  "maxfr",
  "jam",
  "flw",
  "ai",

  // code/data
  "js",
  "c",
  "sql",
  "db",

  // system/executables
  "exe",
  "bin",
  "lnk",
  "job",

  // archives/backups
  "zip",
  "7z",
  "bak",
];

const INLINE_EXTENSIONS_SET = new Set(COMMON_INLINE_FILE_EXTENSIONS);
const DOWNLOAD_EXTENSIONS_SET = new Set(COMMON_DOWNLOAD_FILE_EXTENSIONS);

export function getCanDisplayDocumentInline(
  document: DocumentsState | null | undefined
) {
  if (!document) {
    return { canDisplayInline: false, isUncatalogedFileType: false };
  }

  const fileType = document.fileType.toLowerCase();

  if (!document.isDesignDoc) {
    return { canDisplayInline: true, isUncatalogedFileType: false };
  }

  if (INLINE_EXTENSIONS_SET.has(fileType)) {
    return { canDisplayInline: true, isUncatalogedFileType: false };
  }

  const isKnownDownloadType = DOWNLOAD_EXTENSIONS_SET.has(fileType);

  return {
    canDisplayInline: false,
    isUncatalogedFileType: !isKnownDownloadType,
  };
}

export function logUncatalogedFileType(fileType: string | undefined) {
  if (!fileType) return;
  const logMessage = `Received uncataloged Design Docs file type: ${fileType}`;
  if (isDev) {
    // eslint-disable-next-line
    console.warn(logMessage);
  } else {
    bugsnagClient?.notify(logMessage, (ev) => {
      ev.severity = "warning";
    });
  }
}

export function useCanDisplayDocumentInline(
  document: DocumentsState | null | undefined
) {
  const fileType = document?.fileType.toLowerCase();

  return React.useMemo(() => {
    const { canDisplayInline, isUncatalogedFileType } =
      getCanDisplayDocumentInline(document);

    return {
      canDisplayInline,
      logUncatalogedFileType: isUncatalogedFileType
        ? () => logUncatalogedFileType(fileType)
        : null,
    };
  }, [document, fileType]);
}
